import {
  opportunitiesPipelinesListOptions,
  PaginatedReturnOpportunityListReadable,
  ReturnOpportunityReadable
} from '@crm/api'
import { OpportunityStatuses } from '@crm/components'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { composePrice } from '@crm/utils'
import { defaultNonPagination } from '@crm/utils/filters'
import { ConditionalText, DayLabel, TableContainer } from '@shared/components'
import { useLocales } from '@shared/locales'
import { displayDate } from '@shared/utils'
import { useQuery } from '@tanstack/react-query'
import { TableColumnsType } from 'antd'
import { ReactNode, useState } from 'react'

import { OpportunityActionsButton } from './OpportunityActionsButton'
import { OpportunitiesActionsModals } from './OpportunityActionsModals'

import { ModalState } from './types'

type Props = {
  data?: PaginatedReturnOpportunityListReadable
  isLoading?: boolean
  leadId?: number
  leftChildren: ReactNode
  rightChildren: ReactNode
}

export const OpportunitiesTable = ({
  data,
  isLoading,
  leadId,
  leftChildren,
  rightChildren
}: Props) => {
  const navigate = useAppNavigate()
  const { t } = useLocales()

  const [modalState, setModalState] = useState<ModalState>({
    opportunity: null,
    type: null
  })

  const { data: opportunityPipelines } = useQuery(
    opportunitiesPipelinesListOptions({ query: { ...defaultNonPagination } })
  )

  const onCell = () => ({
    onClick: (e: React.MouseEvent) => e.stopPropagation(),
    onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
    onPointerDown: (e: React.MouseEvent) => e.stopPropagation()
  })

  const onRow = (data: ReturnOpportunityReadable) => ({
    onClick: () => {
      const contextId = data.lead?.id ?? data.client?.id
      navigate(`/opportunities/${data.id}/details/${contextId}`)
    }
  })

  const columns: TableColumnsType<ReturnOpportunityReadable> = [
    {
      title: t('menu.customers'),
      dataIndex: ['client', 'name'],
      key: 'client_name',
      render: (clientName) => (
        <ConditionalText condition={!!clientName}>{clientName}</ConditionalText>
      )
    },
    {
      title: t('filter.leads'),
      dataIndex: ['lead', 'name'],
      key: 'lead_name',
      render: (leadName) => (
        <ConditionalText condition={!!leadName}>{leadName}</ConditionalText>
      )
    },
    {
      title: t('task.product'),
      dataIndex: ['product', 'name'],
      key: 'product',
      render: (product) => (
        <ConditionalText condition={!!product}>{product}</ConditionalText>
      )
    },
    {
      title: t('task.value'),
      dataIndex: 'value',
      key: 'value',
      render: (value, record) => (
        <ConditionalText condition={!!value}>
          {composePrice(value, record?.currency?.symbol)}
        </ConditionalText>
      )
    },

    {
      title: t('task.owner'),
      dataIndex: ['owner', 'name'],
      key: 'owner_name',
      render: (ownerName) => (
        <ConditionalText condition={!!ownerName}>{ownerName}</ConditionalText>
      )
    },
    {
      title: t('filter.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (createdAt) => <DayLabel date={createdAt} />
    },
    {
      title: t('task.close_date'),
      dataIndex: 'close_date',
      key: 'close_date',
      render: (closeDate) => (closeDate ? displayDate(closeDate) : '-')
    },
    ...(opportunityPipelines?.results?.length
      ? ([
          {
            title: t('contacts.pipeline'),
            onCell,
            dataIndex: ['status', 'pipeline', 'name'],
            key: 'pipeline_name',
            render: (pipelineId) => (
              <ConditionalText condition={!!pipelineId}>
                {pipelineId}
              </ConditionalText>
            )
          }
        ] as TableColumnsType<ReturnOpportunityReadable>)
      : []),
    {
      title: t('task.status'),
      onCell,
      render: (_, opportunity) => (
        <OpportunityStatuses opportunity={opportunity} />
      )
    },
    {
      title: t('refusal_reason'),
      dataIndex: ['reason', 'name'],
      key: 'reason_name',
      render: (reasonName) => (
        <ConditionalText condition={!!reasonName}>{reasonName}</ConditionalText>
      )
    },
    {
      width: 50,
      key: 'actions',
      fixed: 'right',
      onCell,
      render: (_, opportunity) => (
        <OpportunityActionsButton
          opportunity={opportunity}
          setModalState={setModalState}
        />
      )
    }
  ]

  return (
    <>
      <TableContainer
        rowKey="id"
        onRow={onRow}
        isHoverAlternative
        loading={isLoading}
        data={data}
        columns={columns}
        showSearch
        leftChildren={leftChildren}
        rightChildren={rightChildren}
      />

      <OpportunitiesActionsModals
        modalState={modalState}
        setModalState={setModalState}
        leadId={
          typeof modalState.opportunity?.lead === 'number'
            ? modalState.opportunity.lead
            : leadId
        }
      />
    </>
  )
}
